import React, { useState, useEffect, useCallback } from 'react';
import { SeriesMetadata } from '../types';
import DataService from '../services/dataService';
import AuthService from '../services/authService';

interface SeriesDashboardProps {
  onSelectSeries: (seriesId: string | null, action: 'existing' | 'new') => void;
}

const SeriesDashboard: React.FC<SeriesDashboardProps> = ({ onSelectSeries }) => {
  const [series, setSeries] = useState<SeriesMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [adminUsers, setAdminUsers] = useState<{ userId: string; email: string | null }[]>([]);
  const [targetUser, setTargetUser] = useState(DataService.getInstance().getTargetUserId() || '');
  const auth = AuthService.getInstance();
  const isAdmin = auth.isAdmin();
  const me = auth.getAuthState().user;

  const dataService = DataService.getInstance();

  const loadUserSeries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userSeries = await dataService.listUserSeries();
      // Sort by creation date, newest first
      const sortedSeries = userSeries.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setSeries(sortedSeries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load series');
    } finally {
      setIsLoading(false);
    }
  }, [dataService]);

  useEffect(() => {
    loadUserSeries();
  }, [loadUserSeries]);

  useEffect(() => {
    if (!isAdmin) return;
    dataService.listAdminUsers().then(setAdminUsers).catch(() => setAdminUsers([]));
  }, [dataService, isAdmin]);

  const handleTargetUser = (userId: string) => {
    setTargetUser(userId);
    dataService.setTargetUserId(userId || null);
    loadUserSeries();
  };

  const handleDeleteSeries = async (seriesId: string) => {
    setDeletingId(seriesId);
    try {
      await dataService.deleteSeries(seriesId);
      await loadUserSeries(); // Refresh the list
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete series');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const viewingEmail = adminUsers.find((u) => u.userId === targetUser)?.email || targetUser;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>{targetUser ? `Series for ${viewingEmail}` : '🏁 Your Racing Series'}</h1>
        <p>{targetUser ? 'Support view of another user’s series' : 'Select an existing series or create a new one'}</p>
        {isAdmin && (
          <div className={`support-switcher${targetUser ? ' is-impersonating' : ''}`}>
            <label htmlFor="admin-user">View as</label>
            <select
              id="admin-user"
              value={targetUser}
              onChange={(e) => handleTargetUser(e.target.value)}
            >
              <option value="">{me?.email || 'My series'}</option>
              {adminUsers.filter((u) => u.userId !== me?.id).map((u) => (
                <option key={u.userId} value={u.userId}>{u.email || u.userId}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={loadUserSeries} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      <div className="dashboard-actions">
        <button 
          className="action-button primary-button"
          onClick={() => onSelectSeries(null, 'new')}
        >
          <span className="button-icon">➕</span>
          Create New Series
        </button>
      </div>

      {isLoading ? (
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading your series...</p>
        </div>
      ) : series.length > 0 ? (
        <div className="series-grid">
          {series.map((seriesItem) => (
            <div key={seriesItem.id} className="series-card">
              <div className="series-card-header">
                <h3 className="series-name">{seriesItem.name}</h3>
                <div className="series-actions">
                  <button
                    className="action-icon delete-button"
                    onClick={() => setShowDeleteConfirm(seriesItem.id)}
                    disabled={deletingId === seriesItem.id}
                    title="Delete series"
                  >
                    {deletingId === seriesItem.id ? '⏳' : '🗑️'}
                  </button>
                </div>
              </div>

              <div className="series-stats">
                <div className="stat">
                  <span className="stat-label">Participants:</span>
                  <span className="stat-value">{seriesItem.participantCount}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Races:</span>
                  <span className="stat-value">{seriesItem.raceCount}</span>
                </div>
                {seriesItem.raceNames && seriesItem.raceNames.length > 0 && (
                  <div className="stat race-names-stat">
                    <span className="stat-label">Race Names:</span>
                    <span className="stat-value race-names">
                      {seriesItem.raceNames.slice(0, 3).join(', ')}
                      {seriesItem.raceNames.length > 3 && ` +${seriesItem.raceNames.length - 3} more`}
                    </span>
                  </div>
                )}
                {seriesItem.bestOfRaces && (
                  <div className="stat">
                    <span className="stat-label">Best of:</span>
                    <span className="stat-value">{seriesItem.bestOfRaces} races</span>
                  </div>
                )}
              </div>

              <div className="series-dates">
                <div className="date-info">
                  <span className="date-label">Created:</span>
                  <span className="date-value">{formatTimeAgo(seriesItem.createdAt)}</span>
                </div>
                <div className="date-info">
                  <span className="date-label">Updated:</span>
                  <span className="date-value">{formatDate(seriesItem.lastModified)}</span>
                </div>
                {seriesItem.publishingSettings?.isPublished && seriesItem.publishingSettings?.publishedUrl && (
                  <div className="date-info published-info">
                    <span className="date-label">Published:</span>
                    <a 
                      href={seriesItem.publishingSettings.publishedUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="published-link"
                    >
                      🌐 View Results
                    </a>
                  </div>
                )}
              </div>

              <div className="series-card-footer">
                <button 
                  className="select-series-button"
                  onClick={() => onSelectSeries(seriesItem.id, 'existing')}
                >
                  <span className="button-icon">📊</span>
                  Select Series
                </button>
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm === seriesItem.id && (
                <div className="delete-confirm-overlay">
                  <div className="delete-confirm-modal">
                    <h4>Delete Series?</h4>
                    <p>Are you sure you want to delete <strong>"{seriesItem.name}"</strong>?</p>
                    <p className="warning-text">This action cannot be undone.</p>
                    <div className="confirm-actions">
                      <button 
                        className="confirm-delete-button"
                        onClick={() => handleDeleteSeries(seriesItem.id)}
                        disabled={deletingId === seriesItem.id}
                      >
                        Delete
                      </button>
                      <button 
                        className="cancel-delete-button"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">🏁</div>
          <h3>No Series Yet</h3>
          <p>Create your first racing series to get started</p>
          <button 
            className="empty-action-button"
            onClick={() => onSelectSeries(null, 'new')}
          >
            <span className="button-icon">➕</span>
            Create Your First Series
          </button>
        </div>
      )}
    </div>
  );
};

export default SeriesDashboard; 